import { type AnalyticsStepCard } from "../../../../../hooks/audit/useAnalyticsStepCards";
import { type Citation } from "../../../../../models/audit/citation";
import {
  stepCompletionFor,
  stepOperationFor,
  verdictRunDate,
  type VerdictRunManifest,
} from "../../lib/deterministicSteps";
import { AnalyticsStepRow } from "../AnalyticsStepRow";
import { PaneSection } from "./PaneSection";

interface KeyWorkflowStepsSectionProps {
  /** Every card the run resolved, starred and not; this section shows the starred. */
  resolved: AnalyticsStepCard[];
  /** The read behind the cards failed, so an empty set states nothing. */
  unreadable: boolean;
  /**
   * The run's log, which is where a step's completion time and the operation
   * behind its glyph come from.
   *
   * The verdict this section is already drawn from carries it, so the rows below
   * cost no read of their own and say the same things the steps tab says — one
   * log, read twice, rather than two sources free to disagree about when a step
   * finished or what it did.
   */
  runManifest: VerdictRunManifest;
  /** The step whose card the host's viewer is showing, where it has one. */
  selectedStepId?: string | null;
  /**
   * Absent on a host with no viewer pane, and the rows are then not controls.
   *
   * The section is rendered both inside the fold-out, where a viewer sits beside
   * it, and inside the sheet, where nothing does. A row that selected a step in
   * the sheet would promise a preview the surface has nowhere to put.
   */
  onSelectStep?: (stepId: string) => void;
  /**
   * Absent where the host owns no write for the flag; the stars are then
   * read-only. Handed the card's own citation, which is what the write acts on —
   * the star is the citation's include flag, and resolving it back from a step
   * row would tie the write to the run-steps read the review pane does not make.
   */
  onToggleStar?: (citation: Citation) => void;
}

/**
 * The starred steps, each as the row every surface draws a step as.
 *
 * **The row is a glyph for what the step did, its name and when it finished,
 * and nothing more.** What the step did in detail — the rule it applied, the
 * records it moved, the sentence the engine wrote about it — is on the card the
 * server rendered for it, and that card is the version the export carries
 * unchanged. Restating any of it here would be two accounts of one step waiting
 * to disagree, so selecting the row opens the card instead of this section
 * drawing a second one.
 *
 * **The rows are the steps tab's rows**, through the same component rather than
 * a copy of its markup: the two lists are the same steps over different subsets,
 * and an auditor comparing them is entitled to see one drawing of a step.
 *
 * Starred is the whole of the selection, and it is the same flag the steps pane
 * reads, resolved by the same function off the same card document, so the two
 * can never disagree about whether a step is starred. The two panes draw the
 * same steps over different subsets — this section the starred ones, the steps
 * pane all of them — off one resolved set, so the starred set here is exactly
 * the starred set there. The star is anchored to the card document, not to a
 * picture a client happens to hold, so a step holds its place in this set for as
 * long as its card document resolves.
 *
 * An empty set means nobody starred a step, and the section is absent for it.
 * That reading holds only where the cards were read: a failed read yields the
 * same empty set and supports no such claim, so it says the cards could not be
 * read instead.
 */
export function KeyWorkflowStepsSection({
  resolved,
  unreadable,
  runManifest,
  selectedStepId,
  onSelectStep,
  onToggleStar,
}: KeyWorkflowStepsSectionProps) {
  if (unreadable) {
    return (
      <PaneSection title="KEY WORKFLOW STEPS">
        <p className="text-sm text-font-secondary">
          The steps behind this result could not be read.
        </p>
      </PaneSection>
    );
  }

  const starred = resolved.filter((card) => card.isKey);
  if (starred.length === 0) {
    return null;
  }

  // Dated from the whole log rather than the starred subset: the date is the
  // run's, and starring or unstarring a step must not move it.
  const runDate = verdictRunDate(runManifest);

  return (
    <PaneSection title="KEY WORKFLOW STEPS">
      <ul className="space-y-1.5">
        {starred.map((card) => {
          const citation = card.citation;
          return (
            <AnalyticsStepRow
              key={card.stepId}
              stepName={card.stepName}
              operation={stepOperationFor(runManifest, card.stepId)}
              completion={stepCompletionFor(runManifest, card.stepId)}
              runDate={runDate}
              starred={card.isKey}
              active={card.stepId === selectedStepId}
              onSelect={
                onSelectStep ? () => onSelectStep(card.stepId) : undefined
              }
              onToggleStar={
                onToggleStar && citation
                  ? () => onToggleStar(citation)
                  : undefined
              }
            />
          );
        })}
      </ul>
    </PaneSection>
  );
}
